import React, {useState, useMemo} from 'react'
import ErrorBanner from '../../components/ErrorBanner'
import LoadingBanner from '../../components/LoadingBanner'

import CreateDocumentModal from '../documents/CreateModal'
import ConfirmDeleteDocumentModal from '../documents/ConfirmDeleteModal'
import EditDocumentModal from '../documents/EditModal'

import Document from '../../models/Document'
import {useProject} from "../../providers/ClientProjectProvider";
import {Link, NavLink, Outlet, useOutletContext, useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {useAuth} from "../../providers/AuthProvider";
import {ProjectPositionProvider} from "../../providers/ProjectPositionProvider";
import ProjectFormProvider, {useProjectForm} from "../../providers/ProjectFormProvider";
import TeamCandidateProvider from '../../providers/TeamCandidateProvider'
import {SelectedTeamCandidate} from '../project-bidding/ProjectShortlistedTeamCandidates'
import PositionCostOverview from "../project-bidding/components/PositionCostOverview";
import PositionFormProvider from "../../providers/PositionFormProvider";
import CreateChangeOrderForm from "../client-lead-tools/change-orders/CreateChangeOrderForm";
import {Badge} from "antd";

const ProjectShortlistTab = (props) => {
    const {auth} = useAuth();

    const context= useOutletContext()
    if (!auth.canManageClients()) {
        return null;
    }
    return (
        <Outlet {...props} context={context}/>
    )
}

const ProjectDocumentsTab = (props) => {
    const {project, isLoading, isProjectUpdating: isDisabled, error, refetch} = useProject();

    const navigate = useNavigate();
    const location = useLocation();

    const [selectedDocument, setSelectedDocument] = useState(null);
    const [shownModal, setShownModel] = useState(null);
    const documents = project?.documents || [];


    const createDocument = async (doc) => {
        // console.log('createDocument');

        let newDocument = await Document.uploadForProject(project, doc);
        if (!newDocument) {
            return;
        }

        TB.toast('New document uploaded.', {type: 'success'});
        await setShownModel(null);
        await setSelectedDocument(null)
    }

    const editDocument = async (doc) => {
        // console.log('editDocument', doc);

        await setSelectedDocument(doc)
        await setShownModel('Edit');
    }

    const updateDocument = async (doc) => {
        // console.log('updateDocument', doc);

        let projectDoc = Document.make({...doc});
        let updatedDoc = await projectDoc.updateForProject(
            project
        );
        if (!updatedDoc) {
            return;
        }

        TB.toast('Document updated.', {type: 'success'});
        await setShownModel(null);
        await setSelectedDocument(null)
    }

    const confirmDeleteDocument = async (doc) => {
        // console.log('confirmDeleteDocument', doc);

        await setSelectedDocument(doc)
        await setShownModel('ConfirmDelete');
    }

    const deleteDocument = async (doc) => {
        // console.log('deleteDocument', doc);

        let projectDoc = Document.make({...doc});
        let deletedDoc = await projectDoc.archiveForProject(project);
        if (!deletedDoc) {
            return;
        }

        TB.toast('Document removed.', {type: 'info'});
        await setShownModel(null);
        await setSelectedDocument(null)
    }

    return (
        <React.Fragment>
            {documents?.length && (
                <div
                    className="max-w-screen-md mx-auto px-12 flex flex-col items-stretch justify-start zero-padding-mobile">
                    <div className="flex flex-col items-stretch py-4 mx-auto">
                        <div className="card p-3 mt-4">
                            <div className="mb-2 flex"
                                 style={{justifyContent: 'space-between', alignItems: 'center'}}>

                                <h3 style={{fontSize: '1.3rem', marginBottom: 0}}>Documents</h3>

                                {/*<button type="button"
						className="btn btn-primary"
						onClick={() => this.newDocument()}>
						<i className="fa fa-plus mr-2"/>
						Upload
					</button>*/}
                            </div>

                            <table className="table table-bordered" style={{marginBottom: 0, minWidth: 600}}>
                                <tbody>
                                {documents.map((doc, i) => (
                                    <tr key={`documentRow${i}`}>
                                        <td style={{fontSize: 14, width: '60%'}}>
                                            {doc.display_name}
                                            {doc.type_name !== 'Miscellaneous' && (<br/>)}
                                            {doc.type_name !== 'Miscellaneous' && (
                                                <span className="badge badge-secondary">{doc.type_name}</span>
                                            )}
                                        </td>
                                        <td style={{fontSize: 14}}>
                                            {TB.moment(doc.uploaded_at).format('MMM D YYYY')}
                                        </td>
                                        <td style={{minWidth: 150}}>
                                            <a href={doc.path}
                                               target="_blank"
                                               className="btn btn-outline-primary btn-sm mr-2">
                                                <i className="fa fa-external-link-alt"/>
                                            </a>
                                            {/*<button type="button"
	    								className="btn btn-primary btn-sm mr-2"
	    								onClick={() => this.editDocument(doc)}>
	    								<i className="fa fa-edit"/>
	    							</button>
	    							<button type="button"
	    								className="btn btn-danger btn-sm"
	    								onClick={() => this.confirmDeleteDocument(doc)}>
	    								<i className="fa fa-trash"/>
	    							</button>*/}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmDeleteDocumentModal
                isVisible={shownModal === 'ConfirmDelete'}
                document={selectedDocument}
                toggle={shouldShow => setShownModel(shouldShow ? 'ConfirmDelete' : null)}
                onConfirm={deleteDocument}
                isDisabled={isDisabled}/>

            <EditDocumentModal
                isVisible={shownModal === 'Edit'}
                document={selectedDocument}
                toggle={shouldShow => setShownModel(shouldShow ? 'Edit' : null)}
                onUpdate={updateDocument}
                isDisabled={isDisabled}/>

            <CreateDocumentModal
                isVisible={shownModal === 'Create'}
                toggle={shouldShow => setShownModel(shouldShow ? 'Create' : null)}
                onSubmit={createDocument}
                isDisabled={isDisabled}/>

        </React.Fragment>
    )
}

const ProjectOverviewTab = (props) => {
    const {project, isLoading, isProjectUpdating: isDisabled, error, refetch} = useProjectForm();
    const {auth, appropriate} = useAuth();

    const [searchParams, setSearchParams] = useSearchParams();

    const handleClose = () => {
        if (isDisabled) {
            return;
        }
        let params = new URLSearchParams(searchParams);
        params.delete('changeOrder');

        return setSearchParams(params)
        // setShowChangeOrderModal(false);
        // setSelectedPosition(null);
        // setChangeOrderType(null);
    }

    const [publishedCOs, unpublishedCOs] = TB.partition(project?.change_orders || [], order => !!order?.published_at)
    if (!project) return null;

    return (
        <div className="flex flex-col items-stretch gap-6">

            <div className="bg-white px-4 py-6 mb-10">

                <div className="container mx-auto max-w-[1225px]">

                    <div className={TB.mapClass("grid gap-y-6 gap-x-2", {
                        "grid-cols-9": auth.canManageClients(),
                        "grid-cols-4": !auth.canManageClients(),
                    })}>
                        <div className={TB.mapClass("flex flex-col gap-2", {
                            "col-span-2": auth.canManageClients()
                        })}>
                            <p className="text-wrippleLightDarkest font-semibold">
                                Roles:
                            </p>
                            <p className="font-medium text-sm">
                                {/* {project.positions_count} */}
                                {project.isService() ? project.disciplines?.length : project.positions_count}
                            </p>
                        </div>

                        <div className={TB.mapClass("flex flex-col gap-2", {
                            "col-span-2": auth.canManageClients()
                        })}>
                            <p className="text-wrippleLightDarkest font-semibold">
                                Start Date:
                            </p>
                            <p className="font-medium text-sm">
                                {TB.moment(project.anticipated_started_at).format('M/D/YYYY')}
                            </p>
                        </div>

                        <div className={TB.mapClass("flex flex-col gap-2", {
                            "col-span-2": auth.canManageClients()
                        })}>
                            <p className="text-wrippleLightDarkest font-semibold">
                                End Date:
                            </p>
                            <p className="font-medium text-sm">
                                {TB.moment(project.anticipated_ended_at).format('M/D/YYYY')}
                            </p>
                        </div>

                        <div className={TB.mapClass("flex flex-row flex-wrap justify-between items-start", {
                            "col-span-3": auth.canManageClients()
                        })}>
                            <div className="flex flex-col gap-2">
                                <p className="text-wrippleLightDarkest font-semibold">
                                    Change Orders:
                                </p>
                                <p className="font-medium text-sm">
                                    {publishedCOs?.length || 0} Published, <span
                                    className={unpublishedCOs.length ? 'text-danger' : ''}>{unpublishedCOs?.length || 0} Pending</span>
                                </p>
                            </div>
                            {auth?.canManageClients() && project.isTimeMaterials() && (
                                <button
                                    disabled={isDisabled}
                                    onClick={async () => {
                                        if (TB.find(project.change_orders || [], ['published_at', null])) {
                                            return TB.alert("alert", "Existing Change Order Not Published", "There is already a change order that hasn't been published. Edit and publish the existing change order in the Contract History tab before creating a new one", [{title: 'Ok'}])
                                        }

                                        let params = new URLSearchParams(searchParams);

                                        params.set('changeOrder', 'new');

                                        return setSearchParams(params);
                                    }}
                                    className={TB.mapClass("whitespace-nowrap btn btn-primary px-2 text-xs", {
                                        "disabled-edit-btn": TB.find(project.change_orders || [], ['published_at', null])
                                    })}>
                                    Create Change Order
                                </button>
                            )}
                        </div>

                        <div className={TB.mapClass("flex flex-col gap-2", {
                            "col-span-2": auth.canManageClients()
                        })}>
                            <p className="text-wrippleLightDarkest font-semibold">
                                Original Contract Value:
                            </p>
                            <p className="font-medium text-sm">
                                {TB.formatCurrency(project.original_contract_value, false)}
                            </p>
                        </div>
                        <div className={TB.mapClass("flex flex-col gap-2", {
                            "col-span-2": auth.canManageClients()
                        })}>
                            <p className="text-wrippleLightDarkest font-semibold">
                                Contract Value To Date:
                            </p>
                            <p className="font-medium text-sm">
                                {TB.formatCurrency(project.contract_value, false)}
                            </p>
                        </div>
                        {auth.canManageClients() && (
                            <React.Fragment>
                                <div className="flex flex-col gap-2 col-span-2">
                                    <p className="text-wrippleLightDarkest font-semibold">
                                        Original Talent Cost:
                                    </p>
                                    <p className="font-medium text-sm">
                                        {TB.formatCurrency(project.original_talent_cost, false)}
                                    </p>
                                </div>
                                <div className="col-span-3 flex flex-col gap-2">
                                    <p className="text-wrippleLightDarkest font-semibold">
                                        Talent Cost To Date:
                                    </p>
                                    <p className="font-medium text-sm">
                                        {TB.formatCurrency(project.talent_cost, false)}
                                    </p>
                                </div>
                            </React.Fragment>
                        )}
                    </div>
                </div>
            </div>

            <div className="w-full px-4 pb-6 mx-auto max-w-[1250px]">
                {!isLoading && project && project.isService() && (
                    <TeamCandidateProvider team={project.assigned_team_candidate}>
                        <SelectedTeamCandidate active/>
                    </TeamCandidateProvider>
                )}

                {!isLoading && project && project.positions?.length > 0 && TB.map(project.positions, (position, index) => {
                    const displayIndex = index + 1;
                    return (
                        <ProjectPositionProvider key={`ppr-${position.id}`} positionId={position.id}>
                            <div className="shortlisted-candidates-card container mx-auto max-w-[1225px]">

                                <div className="role-header flex-row items-stretch justify-between">
                                    <div className="flex flex-col gap-4 items-start flex-grow">
                                        <div className="flex flex-row items-center justify-start">
                                            <h6>
                                                Role {displayIndex} of {project?.sortedPositions()?.length}
                                            </h6>

                                        </div>
                                        <div className="flex flex-row items-center self-stretch">
                                            <h3>{position.title}</h3>
                                        </div>
                                    </div>

                                </div>
                                <PositionFormProvider positionId={position.id}>
                                    <PositionCostOverview
                                        position={position}
                                        canEdit={false}
                                        isDisabled={isDisabled}/>
                                </PositionFormProvider>

                            </div>
                            {displayIndex < project.positions.length && <hr className="mx-9"/>}

                        </ProjectPositionProvider>
                    )
                })}
            </div>

            {searchParams.has('changeOrder') && !isLoading && project && <CreateChangeOrderForm/>}
        </div>
    )
}
const ProjectContractHistoryTab = (props) => {
    const {auth} = useAuth();
    const {project, isLoading, isDisabled} = useProjectForm();
    const [searchParams, setSearchParams] = useSearchParams();

    const changeOrders = useMemo(() => {
        // if (!auth.canManageClients()) {
        //     return TB.filter(project?.change_orders || [], order => !!order.published_at)
        // }
        return project?.change_orders || [];
    }, [isDisabled, project?.change_orders, auth]);


    if (!project) return null;
    return (
        <div className="flex flex-col items-stretch gap-6 py-8">
            <div className="container mx-auto max-w-[1225px]">

            <div className="bg-white px-4 py-6 mb-10">
                <h3 style={{fontSize: '1.3rem', marginBottom: 15}}>Contract History</h3>

                    <table className="table">
                        <thead>
                        <tr>
                            <th className="border-0 text-left"/>
                            <th className="border-0 text-left text-xs">Effective Date</th>
                            <th className="border-0 text-left text-xs">Notes</th>
                            <th className="border-0 text-left text-xs">Resources Affected</th>
                            <th className="border-0 text-left text-xs">Total Hours</th>
                            <th className="border-0 text-left text-xs">Contract Value</th>
                            {auth.isClientConsultant() && (
                                <th className="border-0 text-left text-xs">Resource Cost</th>
                            )}
                            <th className="border-0 text-left"/>
                        </tr>
                        </thead>

                        <tbody className="table-bordered">
                        <tr>
                            <td className="border-0 flex justify-between items-center">
                                <span className="text-xs">
                                    Original Contract
                                </span>
                            </td>
                            <td>
                                <span className="text-xs">
                                    {TB.moment(project.started_at).format('M/D/YYYY')}
                                </span>
                            </td>
                            <td></td>
                            <td>
                                <span className="text-xs">
                                    {project.positions?.length}
                                </span>
                            </td>
                            <td>
                                <span className="text-xs">
                                    {TB.sumBy(project.positions || [], 'original_total_periods')}
                                </span>
                            </td>
                            <td>
                                <span className="text-xs">
                                    ${TB.formatNumber(project.original_contract_value, 2)}
                                </span>
                            </td>
                            {auth.isClientConsultant() && (
                                <td>
                                    <span className="text-xs">
                                        ${TB.formatNumber(project.original_talent_cost, 2)}
                                    </span>
                                </td>
                            )}

                            <td className="max-w-[100px]" />
                        </tr>

                        {changeOrders?.length > 0 && TB.map(TB.orderBy(changeOrders, 'id', 'asc'), (co, i) => {
                            const hourImpact = TB.sumBy(co.items, item => item.new_total_periods - item.original_total_periods);
                            const resourceCostImpact = TB.sumBy(co.items, 'net_talent_cost');
                            const contractValueImpact = TB.sumBy(co.items, 'net_contract_value');
                            return (
                                <tr key={co.id} className={co.published_at ? "bg-gray-50" : "bg-red-50"}>
                                    <td>
                                        <div className="flex justify-between items-center">
                                            <span className="text-xs">
                                                {co.name}
                                            </span>
                                        </div>
                                    </td>

                                    <td>
                                        <span className="text-xs">
                                            {co.published_at ? TB.moment(co.started_at).format('M/D/YYYY') : (
                                                <Badge color={TB.config('color.danger')} count={'Not Published'}/>
                                            )}
                                        </span>
                                    </td>
                                    <td className="max-w-[280px]">
                                        <p className="text-xxs line-clamp-3 text-ellipsis overflow-hidden">
                                            {co.notes || <i className="opacity-60">No Notes Provided</i>}
                                        </p>
                                    </td>
                                    <td>
                                        <span className="text-xs">
                                            {co.items?.length || 0}
                                        </span>
                                    </td>
                                    <td>
                                        <p className={hourImpact !== 0 ? 'font-semibold' : ''}>
                                            <span className="text-xs">
                                                {hourImpact >= 0 ? '+' : '-'}
                                                {Math.abs(hourImpact)}
                                            </span>
                                        </p>
                                    </td>
                                    <td>
                                        <p className={contractValueImpact !== 0 ? 'font-semibold' : ''}>
                                            <span className="text-xs">
                                                {contractValueImpact >= 0 ? '+' : '-'}
                                                ${TB.formatNumber(Math.abs(contractValueImpact), 2)}
                                            </span>
                                        </p>
                                    </td>

                                    {auth.isClientConsultant() && (
                                        <td>
                                            <p className={resourceCostImpact !== 0 ? 'font-semibold' : ''}>
                                                <span className="text-xs">
                                                    {resourceCostImpact >= 0 ? '+' : '-'}
                                                    ${TB.formatNumber(Math.abs(resourceCostImpact), 2)}
                                                </span>
                                            </p>
                                        </td>
                                    )}

                                    <td className="max-w-[100px]">
                                        {co.published_at ? (
                                            <button type="button" onClick={() => {
                                                let params = new URLSearchParams(searchParams);

                                                params.set('changeOrder', co.id);

                                                return setSearchParams(params);
                                            }} className="btn btn-link-primary p-0 mx-auto text-xs">
                                                View
                                            </button>
                                        ) : (
                                            <button type="button" onClick={() => {
                                                // if (auth.canManageClients()) {
                                                    let params = new URLSearchParams(searchParams);

                                                    params.set('changeOrder', co.id);

                                                    return setSearchParams(params);
                                                // }
                                            }} className="btn btn-link-primary p-0 mx-auto text-xs">
                                                {auth.canManageClients() ? 'Edit' : 'View'}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            )
                        })}
                            <tr className="bg-[#595959] text-white">

                                <td className="border-0 flex justify-between items-center">
                                    <span className="text-xs">
                                        Running Total
                                    </span>
                                </td>
                                <td>
                                    <span className="text-xs">
                                        {/*{TB.moment(project.started_at).format('M/D/YYYY')}*/}
                                    </span>
                                </td>
                                <td/>
                                <td>
                                    <span className="text-xs">
                                        {project.positions?.length}
                                    </span>
                                </td>
                                <td>
                                    <span className="text-xs">
                                        {TB.sumBy(project.positions || [], 'actual_total_periods')}
                                    </span>
                                </td>
                                <td>
                                    <span className="text-xs">
                                        ${TB.formatNumber(project.contract_value, 2)}
                                    </span>
                                </td>
                                {auth.isClientConsultant() && (
                                    <td>
                                        <span className="text-xs">
                                            ${TB.formatNumber(project.talent_cost, 2)}
                                        </span>
                                    </td>
                                )}

                                <td className="max-w-[100px]" />
                            </tr>
                        </tbody>
                    </table>

                    {/*<ModalHeadless isVisible={isModalOpen} onCloseAction={() => setIsModalOpen(false)} closeOnBackdrop>*/}
                    {/*    {selectedChangeOrder && (*/}
                    {/*        <ChangeOrderViewModal*/}
                    {/*            changeOrder={selectedChangeOrder}*/}
                    {/*            onClose={() => setIsModalOpen(false)}*/}
                    {/*        />*/}
                    {/*    )}*/}
                    {/*</ModalHeadless>*/}
                </div>
            </div>

            {searchParams.has('changeOrder') && !isLoading && project && <CreateChangeOrderForm/>}
        </div>
    )
}

const ProjectDetail = (props) => {
    const {project, isLoading, isProjectUpdating: isDisabled, error, refetch} = useProject();
    const {auth, appropriate} = useAuth()

    const location = useLocation();
    if (isLoading) {
        return (
            <div className="flex flex-col items-center py-44">
                <LoadingBanner message="Loading Project"/>
            </div>
        )
    }

    const renderBackBtn = () => {

        let back = {
            title: 'Projects',
            path: '/client/projects/active-complete',
        }

        let btnStyles = {
            alignSelf: 'center',
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            padding: '0 16px 10px 0',
            // padding: 0,
            color: '#F5F6F8',
            maxWidth: '100%'
        };

        if (TB.isObject(back)
            && (TB.isString(back.path) || TB.isObject(back.path))) {
            return (
                <Link to={back.path}
                      style={btnStyles}>
                    <i className="fa fa-arrow-left mr-2"/>
                    {back.title || 'Back'}
                </Link>
            );
        }


        if ((TB.isObject(back) && !back.path) || (TB.isBoolean(back) && back)) {
            return (
                <button type="button"
                        className="btn btn-link"
                        style={btnStyles}
                        onClick={() => navigate(-1)}>
                    <i className="fa fa-arrow-left mr-2"/>
                    {back.title || 'Back'}
                </button>
            );
        }


        return <div/>;
    }
    return (
        <div className="flex flex-col items-stretch ">
            {!location.pathname.includes('talent-profiles') && (
                <div className="nav-container">
                    <div className="nav-info">

                        {renderBackBtn()}

                        <div className="flex-grow flex-2 flex flex-col items-center gap-3">
                            <div style={{
                                ...styles.titleContainer,
                                justifyContent: 'center'
                            }}>
                                <h2 className="inline mr-3 mb-0 text-white"
                                    style={{fontWeight: 300, fontSize: '1.5rem', marginBottom: 0, color: 'white'}}>
                                    {project?.name}
                                </h2>

                            </div>
                            <div className="self-stretch flex flex-row gap-4 items-center justify-around">
                                <NavLink

                                    className={({isActive}) => isActive ? "underline text-white font-semibold" : "hover:underline text-white font-semibold"}
                                    to="brief">
                                    Brief
                                </NavLink>
                                <NavLink

                                    className={({isActive}) => isActive ? "underline text-white font-semibold" : "hover:underline text-white font-semibold"}
                                    to="overview">
                                    Overview
                                </NavLink>
                                {project?.isTimeMaterials() && (
                                    <NavLink
                                        className={({isActive}) => isActive ? "underline text-white font-semibold" : "hover:underline text-white font-semibold"}
                                        to="contract-history">
                                        Contract History
                                    </NavLink>
                                )}
                                {auth.canManageClients() && (
                                    <NavLink
                                        className={({isActive}) => isActive ? "underline text-white font-semibold" : "hover:underline text-white font-semibold"}
                                        to="short-list">
                                        Original Shortlist
                                    </NavLink>
                                )}
                                <NavLink
                                    className={({isActive}) => isActive ? "underline text-white font-semibold" : "hover:underline text-white font-semibold"}
                                    to="documents">
                                    Documents
                                </NavLink>
                            </div>
                        </div>
                        <div style={{flex: 1, textAlign: 'right', display: 'flex', flexDirection: 'column'}}/>

                    </div>
                </div>
            )}

            {isLoading && <LoadingBanner message="Loading Project"/>}

            {TB.isObject(error) && (
                <ErrorBanner {...error}
                             onRetry={refetch}/>
            )}

            {!isLoading && !TB.isObject(error) && (
                <ProjectFormProvider>
                    <Outlet context={{
                        toggleUsability: () => true,
                        queryParams: {},
                        isDisabled: false,
                        goToStep: () => true
                    }}/>
                </ProjectFormProvider>
            )}
        </div>
    )
}

const styles = {
    container: {
        // background: '#323C47',
        alignItems: 'center',
    },
    backBtn: {
        // color: '#F5F6F8',
        alignSelf: 'center',
        flex: 1,
    },
    titleContainer: {
        color: 'white',
        margin: '0 auto',
        // flex: 1,
        maxWidth: '100%',
        display: 'flex',
        alignItems: 'center',
    },
};
export default ProjectDetail;

export {
    ProjectContractHistoryTab,
    ProjectDocumentsTab,
    ProjectOverviewTab,
    ProjectShortlistTab
}